import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Tabs,
  Row,
  Col,
  Button,
  Typography,
  Image as AntImage,
  Tooltip,
  Spin,
  Input,
} from "antd";
import type { TabsProps } from "antd";
import {
  DownloadOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  ReadOutlined,
  SolutionOutlined,
  BankOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileUnknownOutlined,
  EyeOutlined,
  InboxOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import useApi from "../../../core/customhook/useApi";
import { apibaseurl } from "../../../core/webservice/Webservice_url";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { PageBreadcrumb } from "../../../core/common/BreadCrumbForm";

const { Title, Text } = Typography;

const breadcrumbItem = [
  { label: "Applicant Submissions" },
  { label: "Candidate Application Details" },
];

interface CandidateApplicationFormLocationState {
  assignment_id: string;
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

// One icon per check type. No per-category colours: colour is reserved
// for status only, so a reviewer reads red/amber/green as meaning.
const getSectionIcon = (name?: string): React.ReactNode => {
  const key = (name || "").toLowerCase();
  if (key.includes("personal")) return <UserOutlined />;
  if (key.includes("aadhaar") || key.includes("kyc"))
    return <SafetyCertificateOutlined />;
  if (key.includes("pan")) return <IdcardOutlined />;
  if (/education|degree|academic|marksheet|pg/.test(key)) return <ReadOutlined />;
  if (/employ|experience|work|pay/.test(key)) return <SolutionOutlined />;
  if (/address|bank/.test(key)) return <BankOutlined />;
  if (/criminal|court|police/.test(key)) return <FileProtectOutlined />;
  return <FileSearchOutlined />;
};

type StatusTone = "ok" | "wait" | "bad";

const StatusTag: React.FC<{ tone: StatusTone; label: string }> = ({
  tone,
  label,
}) => (
  <span className={`cad-status cad-status-${tone}`}>
    <i className="cad-status-dot" />
    {label}
  </span>
);

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const getFileExtension = (name?: string) => {
  if (!name) return "";
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

const getPreviewFileType = (
  mimeType?: string,
  name?: string,
): "image" | "pdf" | "other" => {
  const type = (mimeType || "").toLowerCase();
  const ext = getFileExtension(name);
  if (type.includes("pdf") || ext === "pdf") return "pdf";
  if (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  return "other";
};

const getFileIcon = (mimeType?: string, name?: string) => {
  const kind = getPreviewFileType(mimeType, name);
  if (kind === "pdf") return <FilePdfOutlined className="cad-file-icon-pdf" />;
  if (kind === "image") return <FileImageOutlined />;
  const fileName = (name || "").toLowerCase();
  if ((mimeType || "").includes("word") || /\.(doc|docx)$/.test(fileName))
    return <FileWordOutlined />;
  return <FileUnknownOutlined />;
};

const formatFileSize = (bytes?: string | number) => {
  const size = Number(bytes);
  if (!size || isNaN(size)) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatFileDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

const isScanned = (status?: string) =>
  ["completed", "clean", "passed"].includes((status || "").toLowerCase());
const isScanFailed = (status?: string) =>
  ["failed", "infected", "rejected"].includes((status || "").toLowerCase());

const getScanStatusMeta = (status?: string): { label: string; tone: StatusTone } => {
  if (isScanned(status)) return { label: "Scanned", tone: "ok" };
  if (isScanFailed(status)) return { label: "Scan failed", tone: "bad" };
  return { label: "Scan pending", tone: "wait" };
};

const getFileUrl = (file: any) => `${apibaseurl}/uploads/${file.s3_key}`;

/* ------------------------------------------------------------------ *
 * Small presentational pieces
 * ------------------------------------------------------------------ */

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="cad-field">
    <span className="cad-field-label">{label}</span>
    <span className="cad-field-value">{value}</span>
  </div>
);

const EmptyNote: React.FC<{ text: string }> = ({ text }) => (
  <div className="cad-empty">
    <InboxOutlined />
    <span>{text}</span>
  </div>
);

const Section: React.FC<{
  title: string;
  meta?: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, meta, extra, children }) => (
  <section className="cad-section">
    <header className="cad-section-head">
      <span className="cad-section-icon">{getSectionIcon(title)}</span>
      <h3 className="cad-section-title">{title}</h3>
      {meta && <span className="cad-section-meta">{meta}</span>}
      {extra && <div className="cad-section-extra">{extra}</div>}
    </header>
    <div className="cad-section-body">{children}</div>
  </section>
);

// One compact row per file: thumbnail, name, facts, actions.
const FileRow: React.FC<{
  file: any;
  isBroken: boolean;
  onImageError: () => void;
  onDownload: (file: any) => void;
}> = ({ file, isBroken, onImageError, onDownload }) => {
  const url = getFileUrl(file);
  const kind = getPreviewFileType(file.mime_type, file.original_name);
  const scan = getScanStatusMeta(file.scan_status);
  const showImage = kind === "image" && !isBroken;

  return (
    <div className="cad-file">
      <div className="cad-file-thumb">
        {showImage ? (
          <AntImage
            src={url}
            alt={file.original_name || "Document preview"}
            onError={onImageError}
            preview={{ mask: <EyeOutlined /> }}
          />
        ) : (
          getFileIcon(file.mime_type, file.original_name)
        )}
      </div>

      <div className="cad-file-info">
        <Tooltip title={file.original_name}>
          <span className="cad-file-name">{file.original_name || "Document"}</span>
        </Tooltip>
        <span className="cad-file-meta">
          {formatFileSize(file.size_bytes)} · {formatFileDate(file.uploaded_at)}
        </span>
        <StatusTag tone={scan.tone} label={scan.label} />
      </div>

      <div className="cad-file-actions">
        {!showImage && (
          <Tooltip title="Open file">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            />
          </Tooltip>
        )}
        <Tooltip title="Download file">
          <Button
            type="text"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => onDownload(file)}
          />
        </Tooltip>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

const Admin_CandidateApplicationDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { assignment_id } =
    (location.state as CandidateApplicationFormLocationState) || {};

  const [activeTab, setActiveTab] = useState("application");
  const [brokenImageIds, setBrokenImageIds] = useState<Set<string>>(new Set());
  const [docQuery, setDocQuery] = useState("");

  const markImageBroken = (fileId: string) =>
    setBrokenImageIds((prev) => {
      if (prev.has(fileId)) return prev;
      const next = new Set(prev);
      next.add(fileId);
      return next;
    });

  const getCandidateResponse = useApi<any>(
    `/api/v1/candidate/forms/candidate-response/${assignment_id}`,
    { method: "GET", autoCall: false, handleGlobalError: false },
  );
  const getDocumentAPI = useApi<any>(
    `/api/v1/candidate/forms/uploaded-files/${assignment_id}`,
    { method: "GET", autoCall: false, handleGlobalError: false },
  );
  const getConcentAPI = useApi<any>(
    `/api/v1/candidate/forms/concent/${assignment_id}`,
    { method: "GET", autoCall: false, handleGlobalError: false },
  );

  useEffect(() => {
    if (!assignment_id) return;
    getCandidateResponse.callApi();
    getDocumentAPI.callApi();
    getConcentAPI.callApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment_id]);

  const consentPdfUrl = getConcentAPI.data?.data?.pdf_url;
  const consentPdf = consentPdfUrl ? `${apibaseurl}${consentPdfUrl}` : "";
  const candidate = getCandidateResponse.data?.data?.candidate;
  const checks: any[] = getCandidateResponse.data?.data?.checks || [];
  const candidateDocuments: any[] = getDocumentAPI.data?.data || [];

  const formatKey = (key: string) =>
    key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

  const formatValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === "") return "-";
    const lowerKey = key.toLowerCase();
    if (
      lowerKey === "dob" ||
      lowerKey === "date" ||
      lowerKey.endsWith("_date") ||
      lowerKey.endsWith("_at")
    ) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) return date.toLocaleDateString("en-GB");
    }
    return String(value);
  };

  const columns = useMemo(
    () => [
      { title: "Section", dataIndex: "section", key: "section" },
      { title: "Field", dataIndex: "field", key: "field" },
      { title: "Value", dataIndex: "value", key: "value" },
    ],
    [],
  );

  const filteredData = useMemo(() => {
    const rows: { section: string; field: string; value: string }[] = [];
    if (candidate) {
      Object.entries(candidate).forEach(([key, value]) =>
        rows.push({
          section: "Personal details",
          field: formatKey(key),
          value: formatValue(key, value),
        }),
      );
    }
    checks.forEach((check: any) =>
      Object.entries(check.response_data || {}).forEach(([key, value]) =>
        rows.push({
          section: check.check_name,
          field: formatKey(key),
          value: formatValue(key, value),
        }),
      ),
    );
    return rows;
  }, [candidate, checks]);

  const docTotals = useMemo(() => {
    let total = 0;
    let scanned = 0;
    let pending = 0;
    let failed = 0;
    candidateDocuments.forEach((item: any) => {
      Object.values(item.files || {}).forEach((files: any) => {
        (files || []).forEach((file: any) => {
          total += 1;
          if (isScanned(file.scan_status)) scanned += 1;
          else if (isScanFailed(file.scan_status)) failed += 1;
          else pending += 1;
        });
      });
    });
    return { total, scanned, pending, failed };
  }, [candidateDocuments]);

  const checksSubmitted = useMemo(
    () =>
      checks.filter((c: any) => Object.keys(c.response_data || {}).length > 0)
        .length,
    [checks],
  );

  const handleDownloadFile = async (file: any) => {
    try {
      const response = await fetch(getFileUrl(file), { method: "GET" });
      if (!response.ok) throw new Error("Failed to download file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.original_name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download file", err);
    }
  };

  const handleDownloadExcel = () =>
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Candidate_Details_Report",
      sheetName: "Candidate Details",
    });

  /* ---------------- Tabs ---------------- */

  const renderApplicationTab = () => (
    <>
      <div className="cad-summary">
        <div className="cad-summary-item">
          <strong>
            {checksSubmitted}
            <span>/{checks.length}</span>
          </strong>
          <span>Checks with data</span>
        </div>
        <div className="cad-summary-item">
          <strong>{docTotals.total}</strong>
          <span>Documents uploaded</span>
        </div>
        <div className="cad-summary-item">
          <strong>
            {docTotals.scanned}
            <span>/{docTotals.total}</span>
          </strong>
          <span>Files scanned</span>
        </div>
      </div>

      {candidate && (
        <Section title="Personal details" meta="Profile submitted by candidate">
          <div className="cad-field-grid">
            {Object.entries(candidate).map(([key, value]) => (
              <Field
                key={key}
                label={formatKey(key)}
                value={formatValue(key, value)}
              />
            ))}
          </div>
        </Section>
      )}

      {checks.map((check: any) => {
        const entries = Object.entries(check.response_data || {});
        return (
          <Section
            key={check.assignment_template_id}
            title={check.check_name}
            meta="Verification check"
            extra={
              entries.length ? (
                <StatusTag tone="ok" label="Data submitted" />
              ) : (
                <StatusTag tone="wait" label="Awaiting data" />
              )
            }
          >
            {entries.length === 0 ? (
              <EmptyNote text="The candidate has not submitted data for this check yet." />
            ) : (
              <div className="cad-field-grid">
                {entries.map(([key, value]) => (
                  <Field
                    key={key}
                    label={formatKey(key)}
                    value={formatValue(key, value)}
                  />
                ))}
              </div>
            )}
          </Section>
        );
      })}

      <div className="cad-footer">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadExcel}
        >
          Download report
        </Button>
      </div>
    </>
  );

  const renderConsentTab = () =>
    consentPdf ? (
      <iframe
        src={`${consentPdf}#view=FitH`}
        title="Consent document"
        className="cad-consent"
      />
    ) : (
      <div className="cad-consent cad-consent-loading">
        <Spin tip="Loading consent document" />
      </div>
    );

  const renderDocumentsTab = () => {
    if (candidateDocuments.length === 0) {
      return (
        <EmptyNote text="No documents have been uploaded for this candidate yet." />
      );
    }

    const query = docQuery.trim().toLowerCase();

    const sections = candidateDocuments
      .map((item: any, idx: number) => {
        const groups = Object.entries(item.files || {});
        const totalFiles = groups.reduce(
          (count, [, files]: any) => count + (files?.length || 0),
          0,
        );
        const sectionMatches =
          !query || formatKey(item.check).toLowerCase().includes(query);

        const visibleGroups = groups
          .map(([docType, files]: any) => {
            if (!query || sectionMatches) return [docType, files];
            if (formatKey(docType).toLowerCase().includes(query))
              return [docType, files];
            return [
              docType,
              (files || []).filter((f: any) =>
                (f.original_name || "").toLowerCase().includes(query),
              ),
            ];
          })
          .filter(([, files]: any) => files && files.length > 0);

        return { item, idx, visibleGroups, totalFiles };
      })
      .filter(({ visibleGroups }) => visibleGroups.length > 0);

    return (
      <>
        <div className="cad-toolbar">
          <Input
            allowClear
            className="cad-search"
            placeholder="Search by check, document type or file name"
            prefix={<SearchOutlined />}
            value={docQuery}
            onChange={(e) => setDocQuery(e.target.value)}
          />
          <div className="cad-toolbar-stats">
            <StatusTag tone="ok" label={`${docTotals.scanned} scanned`} />
            {docTotals.pending > 0 && (
              <StatusTag tone="wait" label={`${docTotals.pending} pending`} />
            )}
            {docTotals.failed > 0 && (
              <StatusTag tone="bad" label={`${docTotals.failed} failed`} />
            )}
          </div>
        </div>

        {sections.length === 0 ? (
          <EmptyNote text="No documents match this search. Try a different name or check." />
        ) : (
          sections.map(({ item, idx, visibleGroups, totalFiles }) => (
            <Section
              key={item.check ?? idx}
              title={formatKey(item.check)}
              meta={`${totalFiles} file${totalFiles === 1 ? "" : "s"}`}
            >
              <Row gutter={[24, 20]}>
                {visibleGroups.map(([docType, files]: any) => (
                  <Col xs={24} lg={12} key={docType}>
                    <div className="cad-doc-group-label">
                      {formatKey(docType)}
                    </div>
                    <div className="cad-file-list">
                      {files.map((file: any) => (
                        <FileRow
                          key={file.file_id}
                          file={file}
                          isBroken={brokenImageIds.has(file.file_id)}
                          onImageError={() => markImageBroken(file.file_id)}
                          onDownload={handleDownloadFile}
                        />
                      ))}
                    </div>
                  </Col>
                ))}
              </Row>
            </Section>
          ))
        )}
      </>
    );
  };

  const tabItems: TabsProps["items"] = [
    {
      key: "application",
      label: "Application",
      children: renderApplicationTab(),
    },
    { key: "consent", label: "Consent", children: renderConsentTab() },
    {
      key: "candidateDocuments",
      label: docTotals.total
        ? `Documents (${docTotals.total})`
        : "Documents",
      children: renderDocumentsTab(),
    },
  ];

  if (!assignment_id) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="cad-shell">
            <EmptyNote text="No candidate selected. Go back and open a candidate from the submissions list." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageBreadcrumb
          title="Candidate Application Details"
          breadcrumbs={breadcrumbItem}
        />

        <Card className="cad-shell">
          <header className="cad-header">
            <div>
              <Title level={4} className="cad-title">
                Candidate verification
              </Title>
              <Text type="secondary">
                Profile, verification checks and uploaded documents ·{" "}
                <span className="cad-id">{assignment_id}</span>
              </Text>
            </div>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
          </header>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="cad-tabs"
          />
        </Card>
      </div>
    </div>
  );
};

export default Admin_CandidateApplicationDetails;